import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Heading,
  Text,
  Box,
  Flex,
  Toast,
  Modal,
  ModalHeader,
  ModalContent,
  ModalFooter,
  TextField,
} from '@vibe/core'
import { Add, Search, Edit, Delete, Show } from '@vibe/icons'
import { Layout } from '../components/layout'
import { AuthGuard } from '../components/common/AuthGuard'
import { apiService } from '../services/api'
import { getErrorMessage } from '../utils/errorHandling'
import { PermissionGate } from '../components/common/PermissionGate'
import { JobDefinitionList, JobDefinitionForm } from '../components/forms'
import type { JobDefinition, JobDefinitionFormData } from '../types'
import { PermissionResource, PermissionAction } from '../types'

interface JobDefinitionManagementState {
  jobDefinitions: JobDefinition[]
  loading: boolean
  error: string | null
  searchTerm: string
  showEditModal: boolean
  showDeleteModal: boolean
  selectedJobDefinition: JobDefinition | null
  totalItems: number
  nextKey?: string
  hasMore: boolean
}

export const JobDefinitionManagement: React.FC = () => {
  const navigate = useNavigate()

  const [state, setState] = useState<JobDefinitionManagementState>({
    jobDefinitions: [],
    loading: true,
    error: null,
    searchTerm: '',
    showEditModal: false,
    showDeleteModal: false,
    selectedJobDefinition: null,
    totalItems: 0,
    hasMore: false,
  })

  const [toast, setToast] = useState<{
    message: string
    type: 'positive' | 'negative'
  } | null>(null)

  // Load job definitions
  const loadJobDefinitions = useCallback(
    async (search?: string, reset = true) => {
      try {
        setState(prev => ({ ...prev, loading: true, error: null }))

        const params = {
          size: 20,
          active_only: true,
          ...(search && { search }),
          ...(reset ? {} : { start: state.nextKey }),
        }

        const response = await apiService.getJobDefinitions(params)

        setState(prev => ({
          ...prev,
          jobDefinitions: reset
            ? response.data?.items || []
            : [...prev.jobDefinitions, ...(response.data?.items || [])],
          totalItems: response.data?.total_items || 0,
          nextKey: response.data?.next_key,
          hasMore: !!response.data?.next_key,
          loading: false,
        }))
      } catch (error) {
        console.error('Error loading job definitions:', error)
        setState(prev => ({
          ...prev,
          loading: false,
          error: getErrorMessage(error),
        }))
      }
    },
    [state.nextKey]
  )

  // Initial load
  useEffect(() => {
    loadJobDefinitions()
  }, [])

  // Search handler
  const handleSearch = useCallback(
    (searchTerm: string) => {
      setState(prev => ({ ...prev, searchTerm }))
      loadJobDefinitions(searchTerm, true)
    },
    [loadJobDefinitions]
  )

  // Edit job definition
  const handleEdit = useCallback(
    async (formData: JobDefinitionFormData) => {
      if (!state.selectedJobDefinition) return

      try {
        const updateData = {
          name: formData.name,
          puesto: formData.puesto,
          funciones: formData.funciones,
          perfil: {
            idiomas: formData.idiomas.filter(
              idioma => idioma.lenguaje.trim() && idioma.nivel.trim()
            ),
            estudios: formData.estudios.filter(
              estudio => estudio.nivel.trim() && estudio.detalle.trim()
            ),
            conocimientos: formData.conocimientos.filter(c => c.trim()),
          },
          requisitos: {
            indispensables: formData.requisitos_indispensables.filter(r =>
              r.trim()
            ),
          },
          version: state.selectedJobDefinition.version,
        }

        await apiService.updateJobDefinition(
          state.selectedJobDefinition.id,
          updateData
        )

        setToast({
          message: 'Definición de puesto actualizada exitosamente',
          type: 'positive',
        })

        setState(prev => ({
          ...prev,
          showEditModal: false,
          selectedJobDefinition: null,
        }))

        // Reload the list
        loadJobDefinitions(state.searchTerm, true)
      } catch (error) {
        console.error('Error updating job definition:', error)
        setToast({
          message: getErrorMessage(error),
          type: 'negative',
        })
      }
    },
    [state.selectedJobDefinition, loadJobDefinitions, state.searchTerm]
  )

  // Delete job definition
  const handleDelete = useCallback(async () => {
    if (!state.selectedJobDefinition) return

    try {
      await apiService.deleteJobDefinition(state.selectedJobDefinition.id)

      setToast({
        message: 'Definición de puesto eliminada exitosamente',
        type: 'positive',
      })

      setState(prev => ({
        ...prev,
        showDeleteModal: false,
        selectedJobDefinition: null,
      }))

      // Reload the list
      loadJobDefinitions(state.searchTerm, true)
    } catch (error) {
      console.error('Error deleting job definition:', error)
      setToast({
        message: getErrorMessage(error),
        type: 'negative',
      })
    }
  }, [state.selectedJobDefinition, loadJobDefinitions, state.searchTerm])

  // View job definition details
  const handleView = useCallback((jobDefinition: JobDefinition) => {
    setState(prev => ({
      ...prev,
      selectedJobDefinition: jobDefinition,
    }))
    // Navigate to a detail view or show in modal
    // For now, we'll just show the edit modal in read-only mode
  }, [])

  // Modal handlers
  const openEditModal = useCallback((jobDefinition: JobDefinition) => {
    setState(prev => ({
      ...prev,
      selectedJobDefinition: jobDefinition,
      showEditModal: true,
    }))
  }, [])

  const openDeleteModal = useCallback((jobDefinition: JobDefinition) => {
    setState(prev => ({
      ...prev,
      selectedJobDefinition: jobDefinition,
      showDeleteModal: true,
    }))
  }, [])

  const closeModals = useCallback(() => {
    setState(prev => ({
      ...prev,
      showEditModal: false,
      showDeleteModal: false,
      selectedJobDefinition: null,
    }))
  }, [])

  // Auto-hide toast
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 5000)
      return () => clearTimeout(timer)
    }
  }, [toast])

  // Convert JobDefinition to JobDefinitionFormData for editing
  const convertToFormData = useCallback(
    (jobDefinition: JobDefinition): JobDefinitionFormData => {
      return {
        name: jobDefinition.name,
        puesto: jobDefinition.puesto,
        funciones: jobDefinition.funciones,
        idiomas: jobDefinition.perfil.idiomas,
        estudios: jobDefinition.perfil.estudios,
        conocimientos: jobDefinition.perfil.conocimientos,
        requisitos_indispensables: jobDefinition.requisitos.indispensables,
      }
    },
    []
  )

  return (
    <AuthGuard>
      <PermissionGate
        requiredPermission={{
          resource: PermissionResource.SOLICITUDES,
          action: PermissionAction.READ,
        }}
        fallback={
          <Layout>
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-sm p-6">
                <Text>
                  No tienes permisos para acceder a la gestión de definiciones
                  de puesto.
                </Text>
              </div>
            </div>
          </Layout>
        }
      >
        <Layout>
          <div className="space-y-6">
            {/* Toast notifications */}
            {toast && (
              <Toast
                open={true}
                onClose={() => setToast(null)}
                className="fixed top-4 right-4 z-50"
                type={toast.type}
              >
                {toast.message}
              </Toast>
            )}

            {/* Header */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <Flex justify="space-between" align="center">
                <Box>
                  <Heading type="h1" className="mb-2">
                    Gestión de Definiciones de Puesto
                  </Heading>
                  <Text color="secondary">
                    Crea y administra definiciones de puesto reutilizables para
                    agilizar el proceso de reclutamiento
                  </Text>
                </Box>

                <PermissionGate
                  requiredPermission={{
                    resource: PermissionResource.SOLICITUDES,
                    action: PermissionAction.CREATE,
                  }}
                >
                  <Button onClick={() => navigate('/job-definitions/new')}>
                    <Add /> Nueva Definición
                  </Button>
                </PermissionGate>
              </Flex>
            </div>

            {/* Search and Content */}
            <div className="bg-white rounded-lg shadow-sm">
              {/* Search */}
              <div className="p-6 border-b border-secondary-200">
                <TextField
                  placeholder="Buscar definiciones de puesto..."
                  value={state.searchTerm}
                  onChange={value => handleSearch(value)}
                  className="max-w-md"
                />
              </div>

              {/* Error state */}
              {state.error && (
                <div className="p-6 border-b border-secondary-200">
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <Text className="text-red-700">{state.error}</Text>
                    <Button
                      kind="tertiary"
                      size="small"
                      onClick={() => loadJobDefinitions(state.searchTerm, true)}
                      className="mt-2"
                    >
                      Reintentar
                    </Button>
                  </div>
                </div>
              )}

              {/* Job definitions list */}
              <JobDefinitionList
                jobDefinitions={state.jobDefinitions}
                loading={state.loading}
                hasMore={state.hasMore}
                onLoadMore={() => loadJobDefinitions(state.searchTerm, false)}
                onEdit={openEditModal}
                onDelete={openDeleteModal}
                onView={handleView}
              />
            </div>

            {/* Edit Modal */}
            <Modal show={state.showEditModal} onClose={closeModals}>
              <ModalHeader>
                <Text type="text1" weight="bold">
                  Editar Definición de Puesto
                </Text>
              </ModalHeader>
              <ModalContent>
                {state.selectedJobDefinition ? (
                  <JobDefinitionForm
                    mode="edit"
                    initialData={convertToFormData(state.selectedJobDefinition)}
                    onSubmit={handleEdit}
                    onCancel={closeModals}
                  />
                ) : (
                  <Text>Cargando...</Text>
                )}
              </ModalContent>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal show={state.showDeleteModal} onClose={closeModals}>
              <ModalHeader>
                <Text type="text1" weight="bold">
                  Confirmar Eliminación
                </Text>
              </ModalHeader>
              <ModalContent>
                <Box className="space-y-4">
                  <Text>
                    ¿Estás seguro de que deseas eliminar la definición de puesto
                    "{state.selectedJobDefinition?.name}"?
                  </Text>
                  <Text color="secondary">
                    Esta acción no se puede deshacer. La definición será marcada
                    como inactiva y no estará disponible para nuevas
                    solicitudes.
                  </Text>
                </Box>
              </ModalContent>
              <ModalFooter>
                <Flex justify="end" gap="small">
                  <Button kind="tertiary" onClick={closeModals}>
                    Cancelar
                  </Button>
                  <Button
                    kind="primary"
                    color="negative"
                    onClick={handleDelete}
                  >
                    <Delete /> Eliminar
                  </Button>
                </Flex>
              </ModalFooter>
            </Modal>
          </div>
        </Layout>
      </PermissionGate>
    </AuthGuard>
  )
}

export default JobDefinitionManagement
